"use client";

import React, { useEffect } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useLocale } from "next-intl";
import { useCurrentUser } from "@/modules/auth/context/CurrentUserContext";
import { isGlobalOrMultiRegionTeacher, isTeacherAccount } from "@/modules/auth/utils/userAccess";
import { getRegionLocaleHref } from "@/services/region";

/**
 * Blocks Teacher accounts from every booking route (Free Trial and Course
 * enrollment, including the nested course/teacher step). Booking entry points
 * are already hidden from Teachers in the UI; this closes the gap of reaching
 * a booking page by typing/pasting the URL directly.
 *
 * Frontend-only: the backend still owns the real authorization. We only act
 * once the user has resolved and is confirmed to be a Teacher account — during
 * loading (user null) children render as normal, so Students are never blocked.
 */
export default function TeacherBookingGuard({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const locale = useLocale();
  const { user } = useCurrentUser();
  const isTeacher = isTeacherAccount(user);

  useEffect(() => {
    if (isTeacher) {
      // A Global (or multi-region) teacher has no single canonical region, so
      // don't force one into the target URL — let getRegionLocaleHref fall back
      // to the current URL's region (or none). Single-region teachers keep
      // their existing region-prefixed redirect.
      const regionSlug = isGlobalOrMultiRegionTeacher(user)
        ? undefined
        : (user?.region as { slug?: string } | undefined)?.slug;
      router.replace(getRegionLocaleHref(pathname, locale, "/teacher", regionSlug));
    }
  }, [isTeacher, user, pathname, locale, router]);

  if (isTeacher) {
    // Never render booking content for a Teacher, not even for a single frame.
    return null;
  }

  return <>{children}</>;
}
